import { forwardRef, useEffect, useState, useImperativeHandle, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ConfigProvider, Table as UiTable } from 'antd'
import ruRU from 'antd/lib/locale/ru_RU'
import useRequest from '../../../hooks/useRequest'
import { useDebounce, useFilters } from '../../../hooks'
import { Box } from '../../../ui'
import { getColumns } from '../../helpers'
import { getGenericUsersList, getAdminsUsersList } from '../../config'
import { TableRef, ITypeOption, ELabels, PaginationType, ITable, Filters } from '../../types'
import Filter from '../Filter/Filter'

import style from './style.m.less'
import { useTranslation } from 'react-i18next'

const typeOptions: ITypeOption[] = [
    { label: ELabels.FIZ, id: 0 },
    { label: ELabels.YUR, id: 1 },
    { label: ELabels.IP, id: 2 },
]

const defaultType = typeOptions[0].label

const typeIndex = {
    'Физические лица': 0,
    'Юридические лица': 2,
    ИП: 1,
    1: ELabels.IP,
    2: ELabels.YUR,
    0: ELabels.FIZ,
}

const PAGE_SIZE = 10

const DEFAULT_PAG = {
    page: 1,
    pageSize: PAGE_SIZE,
}

const defaultFilters = {
    email: '',
    full_name: '',
    inn: '',
    login: '',
    snils: '',
    user_role: 0,
    user_type: typeIndex[defaultType],
}

const Table = forwardRef<TableRef, ITable>(({ currentUsers, subTabKey }, ref) => {
    const [typeOption, setTypeOption] = useState<ELabels>(defaultType)
    const [pagination, setPagination] = useState<PaginationType>(() => {
        if (localStorage.getItem('users_pagination')) {
            return JSON.parse(localStorage.getItem('users_pagination'))
        } else {
            localStorage.setItem('users_pagination', JSON.stringify(DEFAULT_PAG))
            return DEFAULT_PAG
        }
    })
    const [filters, setFilters] = useFilters()

    const { t } = useTranslation()

    const location = useLocation()
    const navigate = useNavigate()

    const isGeneric = currentUsers !== 'Административные пользователи'
    const shouldDisplayExtraFilters = currentUsers === 'Пользователи'

    const debouncedFilters = useDebounce(filters)

    const {
        fetch: fetchGeneric,
        result: resultGeneric,
        isLoading: isLoadingGeneric,
    } = useRequest(getGenericUsersList)
    const {
        fetch: fetchAdmins,
        result: resultAdmins,
        isLoading: isLoadingAdmins,
    } = useRequest(getAdminsUsersList)

    const getData = ({ page = 1, pageSize = 10, filters }: PaginationType) => {
        const isNotPhysical =
            typeIndex[typeOption] === 0 &&
            location.pathname !== '/users' &&
            location.pathname !== '/users/admins/physical' &&
            isGeneric

        if (isNotPhysical) {
            return
        }

        ;(isGeneric ? fetchGeneric : fetchAdmins)({
            body: {
                limit: pageSize,
                offset: (page - 1) * pageSize,
                filters,
            },
        })
    }

    const changeTypeOption = (label: ELabels) => {
        setTypeOption(label)
        if (label === 'Физические лица') {
            navigate('/users/admins/physical', {state: {fromMenu: true}})
        } else if (label === 'Юридические лица') {
            navigate('/users/admins/legal', {state: {fromMenu: true}})
        } else {
            navigate('/users/admins/individual', {state: {fromMenu: true}})
        }
    }

    useEffect(() => {
        if (location.state && location.state.fromMenu) {
            setFilters({})
            setTypeOption(defaultType)
            getData({ ...DEFAULT_PAG, filters: defaultFilters })
            navigate(location.pathname, {state: {fromMenu: false}})
        } else {
            getData({ page: pagination.page, pageSize: pagination.pageSize, filters: filters })
            navigate(location.pathname, {state: {fromMenu: false}})
        }
    }, [isGeneric])

    useEffect(() => {
        const filtersAreNotEmpty = debouncedFilters && Object.keys(debouncedFilters).length > 0

        if (!filtersAreNotEmpty) {
            return
        }
        if (location.state && location.state.fromMenu) {
            getData({
                page: 1,
                pageSize: pagination.pageSize,
                filters: {...debouncedFilters, user_type: typeIndex[typeOption]},
            })
            setPagination({page: 1, pageSize: pagination.pageSize})
            localStorage.setItem('users_pagination', JSON.stringify({page: 1, pageSize: pagination.pageSize}))
        } else {
            getData({
                page: pagination.page,
                pageSize: pagination.pageSize,
                filters: filters,
            })
            navigate(location.pathname, {state: {fromMenu: false}})
        }
    }, [debouncedFilters])

    useEffect(() => {
        if (location.state && location.state.fromMenu) {
            setFilters({
                user_type: typeIndex[typeOption],
            })
            getData({
                page: 1,
                pageSize: pagination.pageSize,
                filters: {...filters, user_type: typeIndex[typeOption]},
            })
            setPagination({page: 1, pageSize: pagination.pageSize})
            localStorage.setItem('users_pagination', JSON.stringify({page: 1, pageSize: pagination.pageSize}))
            navigate(location.pathname, {state: {fromMenu: false}})
        } else {
            getData({
                page: pagination.page,
                pageSize: pagination.pageSize,
                filters: filters,
            })
            setPagination({page: pagination.page, pageSize: pagination.pageSize})
            localStorage.setItem('users_pagination', JSON.stringify({page: pagination.page, pageSize: pagination.pageSize}))
        }
    }, [typeOption])

    useEffect(() => {
        if (subTabKey) {
            if (subTabKey === '0') {
                setTypeOption(ELabels.FIZ)
            } else if (subTabKey === '1') {
                setTypeOption(ELabels.IP)
            } else if (subTabKey === '2') {
                setTypeOption(ELabels.YUR)
            }
        }
    }, [subTabKey])

    const updateFilters = (data: string | number | boolean, key: string, obj?: any) => {
        const updatedFilter = {
            ...filters,
            ...(obj ? { ...obj } : { [key]: data }),
        }
        setFilters(updatedFilter)
        setPagination(DEFAULT_PAG)
        localStorage.setItem('users_pagination', JSON.stringify(DEFAULT_PAG))
    }

    useImperativeHandle(ref, () => ({
        updateFilters: (data) => {
            setTypeOption(typeIndex[data])
        },
    }))

    const dataSource = useMemo(() => {
        const result = isGeneric ? resultGeneric : resultAdmins

        if (!result) {
            return []
        }

        return result.users?.map((user: object, index: number) => ({
            ...user,
            key: `id${index}`,
        }))
    }, [resultAdmins, resultGeneric])

    const columns = getColumns(typeIndex[typeOption], isGeneric, t)

    return (
        <div className={style.opened}>
            <Filter
                isGeneric={isGeneric}
                filters={filters}
                onChange={updateFilters}
                typeIndex={typeIndex[typeOption]}
            />
            {shouldDisplayExtraFilters && (
                <div className={style.selector}>
                    {typeOptions.map(({ label, id }) => (
                        <Box direction="row" key={id}>
                            <div
                                className={
                                    typeOption === label ? style.greenToolSelected : style.greenTool
                                }
                                onClick={() => changeTypeOption(label)}
                            >
                                {label}
                            </div>
                            <div className={style.spaceBar}></div>
                        </Box>
                    ))}
                </div>
            )}
            <div className={style.table}>
                <ConfigProvider locale={ruRU}>
                    <UiTable
                        dataSource={dataSource}
                        columns={columns}
                        rowClassName={(record) => (record.has_update ? style.rowBold : '')}
                        pagination={{
                            position: ['bottomLeft'],
                            onChange: (page, pageSize) => {
                                getData({
                                    page,
                                    pageSize,
                                    filters: { user_type: typeIndex[typeOption], ...filters },
                                })
                                setPagination({ page, pageSize })
                                localStorage.setItem('users_pagination', JSON.stringify({ page, pageSize }))
                            },
                            total: isGeneric ? resultGeneric?.total : resultAdmins?.total,
                            current: pagination.page,
                            pageSize: pagination.pageSize,
                            showSizeChanger: true,
                        }}
                        loading={isGeneric ? isLoadingGeneric : isLoadingAdmins}
                        onRow={({ uid }) => ({
                            onClick: () => {
                                navigate(`/user/${uid}`)
                            },
                        })}
                    />
                </ConfigProvider>
            </div>
        </div>
    )
})

export default Table
